/**
 * AGM (Annual General Meeting) 전용 variable mapper
 *
 * AGM_AUTOMATION.md 명세에 정의된 모든 [[variable_name]] 변수를 snake_case 그대로 매핑한다.
 * 자동 파생 변수: fiscal_year_end_label, retiring_director_names_joined
 *
 * 변수 매칭은 case-sensitive 이며 (documentVariableReplacer.js),
 * 문서별로 사용 변수가 다르므로 사용하지 않는 키도 빈 문자열로 포함된다.
 */

export interface RetiringDirectorInput {
  name: string;
  regulation: string;
}

export interface MemberInput {
  name: string;
  address: string;
  /** Address chooser 후보 (length>1 인 경우 UI 가 선택) */
  addresses?: Array<{ type: string; label: string; value: string; position?: string }>;
  /** Backend 매칭에 쓰는 contactCd (chooser 컨텍스트용) */
  contactCd?: string;
}

export interface AGMInput {
  // Company
  companyName: string;
  registrationNo: string;
  /** 구 등록번호 — 헤더 [[if old_registration_no]] ([[old_registration_no]])[[endif]] 치환용 (없으면 빈칸 → 분기 미출력) */
  registrationNoOld?: string;

  // Common dates (DD Month YYYY 포맷으로 들어옴 — UI 에서 변환 후 전달)
  letterDate?: string;
  fiscalYearEndDate?: string;          // e.g. "31 December 2025"
  fiscalYearEndLabel?: string;         // e.g. "YEAR ENDED 31 DECEMBER 2025" (없으면 fiscalYearEndDate에서 자동)
  directorsResolutionDate?: string;
  circulationDate?: string;
  returnDeadline?: string;
  certificationDate?: string;

  // Resolution
  statutoryDeclarantName?: string;
  certifyingSecretaryName?: string;
  resolutionText?: string;

  // Conditions
  directorCount?: number;
  hasConstitution?: boolean;
  constitutionClauseNo?: string;
  /** 결의 근거: Constitution(Clause) / Articles of Association(Article) / Third Schedule(정관 없음) */
  resolutionBasis?: 'constitution' | 'articles' | 'third_schedule';

  // Retiring directors (loop)
  retiringDirectors?: RetiringDirectorInput[];

  // Members (loop)
  members?: MemberInput[];

  // Audited 전용
  auditorFirmName?: string;
  auditorAddress?: string;

  // Unaudited 전용 — Audit Exemption Certificate (LODGER)
  lodgerName?: string;
  lodgerNric?: string;
  lodgerAddress?: string;
  lodgerPhone?: string;
  lodgerEmail?: string;

  // Dividend
  dividendType?: 'interim' | 'final' | '';
  dividendPerShare?: string | number;
  dividendTotalAmount?: string | number;
  dividendRecordDate?: string;
  dividendPaymentDate?: string;

  // Remuneration
  directorsRemunerationTotal?: string | number;
}

function trim(s: any): string {
  return s === null || s === undefined ? '' : String(s).trim();
}

/** Malaysia NRIC 표준 포맷: 12자리 숫자면 XXXXXX-XX-XXXX 로 표시. */
function formatMalaysiaNricForDoc(raw: any): string {
  const s = trim(raw);
  if (!s) return '';
  const digits = s.replace(/\D/g, '');
  if (digits.length === 12) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  return s;
}

function num(v: any): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * fiscalYearEndDate → "YEAR ENDED 31 DECEMBER 2025" 자동 라벨 생성
 * 입력 포맷이 "DD Month YYYY" 또는 ISO(YYYY-MM-DD) 둘 다 처리
 */
function deriveFiscalYearEndLabel(dateStr: string): string {
  if (!dateStr) return '';
  // ISO YYYY-MM-DD
  const iso = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    const months = [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
    ];
    const mi = Math.max(0, Math.min(11, Number(m) - 1));
    return `YEAR ENDED ${Number(d)} ${months[mi]} ${y}`;
  }
  // 이미 "DD Month YYYY" 같은 자연어 — 대문자화만
  return `YEAR ENDED ${dateStr.toUpperCase()}`;
}

export function buildAGMVariables(input: AGMInput): Record<string, any> {
  const companyName = trim(input.companyName);
  const regNo = trim(input.registrationNo);
  const regNoOld = trim(input.registrationNoOld);

  const letterDate = trim(input.letterDate);
  const fiscalYearEndDate = trim(input.fiscalYearEndDate);
  const fiscalYearEndLabel = trim(input.fiscalYearEndLabel) || deriveFiscalYearEndLabel(fiscalYearEndDate);
  const directorsResolutionDate = trim(input.directorsResolutionDate);
  const circulationDate = trim(input.circulationDate);
  const returnDeadline = trim(input.returnDeadline);
  const certificationDate = trim(input.certificationDate);

  const statutoryDeclarantName = trim(input.statutoryDeclarantName);
  const certifyingSecretaryName = trim(input.certifyingSecretaryName);
  const resolutionText = trim(input.resolutionText);

  const directorCount = input.directorCount == null ? 2 : Number(input.directorCount);
  const constitutionClauseNo = trim(input.constitutionClauseNo);
  // 결의 근거(3분기). 미지정 시 기존 hasConstitution(2분기)으로 역호환 매핑.
  const resolutionBasis: 'constitution' | 'articles' | 'third_schedule' =
    input.resolutionBasis
    || (input.hasConstitution === false ? 'third_schedule' : 'constitution');
  const hasConstitution = resolutionBasis !== 'third_schedule';

  const retiringDirectors = (input.retiringDirectors || []).map(r => ({
    retiring_director_name: trim(r.name),
    retiring_director_regulation: trim(r.regulation),
  }));
  const retiringDirectorNamesJoined = retiringDirectors
    .map(r => r.retiring_director_name)
    .filter(Boolean)
    .join(', ');

  const members = (input.members || []).map(m => ({
    member_name: trim(m.name),
    member_address: trim(m.address),
  }));

  const auditorFirmName = trim(input.auditorFirmName);
  const auditorAddress = trim(input.auditorAddress);

  const lodgerName = trim(input.lodgerName);
  const lodgerNric = formatMalaysiaNricForDoc(input.lodgerNric);
  const lodgerAddress = trim(input.lodgerAddress);
  const lodgerPhone = trim(input.lodgerPhone);
  const lodgerEmail = trim(input.lodgerEmail);

  const dividendType = trim(input.dividendType);
  const dividendPerShare = num(input.dividendPerShare);
  const dividendTotalAmount = num(input.dividendTotalAmount);
  const dividendRecordDate = trim(input.dividendRecordDate);
  const dividendPaymentDate = trim(input.dividendPaymentDate);

  const directorsRemunerationTotal = num(input.directorsRemunerationTotal);

  return {
    // ── 회사 ──
    company_name: companyName,
    company_registration_no: regNo,
    old_registration_no: regNoOld, // 헤더 [[if old_registration_no]] 분기용 (없으면 빈칸)

    // ── 공통 날짜 ──
    letter_date: letterDate,
    fiscal_year_end_date: fiscalYearEndDate,
    fiscal_year_end_label: fiscalYearEndLabel,
    directors_resolution_date: directorsResolutionDate,
    circulation_date: circulationDate,
    return_deadline: returnDeadline,
    certification_date: certificationDate,

    // ── 결의 ──
    statutory_declarant_name: statutoryDeclarantName,
    certifying_secretary_name: certifyingSecretaryName,
    resolution_text: resolutionText,

    // ── 조건 플래그 ──
    director_count: directorCount,
    has_constitution: hasConstitution,
    constitution_clause_no: constitutionClauseNo,
    // DCR if 분기 (신규): 이사 수 + 결의 근거 3분기 + clause 번호 별칭
    is_sole_director_company: directorCount === 1,
    is_multiple_director_company: directorCount >= 2,
    resolution_basis_constitution: resolutionBasis === 'constitution',
    resolution_basis_articles: resolutionBasis === 'articles',
    resolution_basis_third_schedule: resolutionBasis === 'third_schedule',
    constitution_clause_number: constitutionClauseNo,

    // ── 이사 재선임 (loop + joined) ──
    retiring_directors: retiringDirectors,
    retiring_director_names_joined: retiringDirectorNamesJoined,

    // ── 멤버 (loop) ──
    members,

    // ── Audited 전용 ──
    auditor_firm_name: auditorFirmName,
    auditor_address: auditorAddress,

    // ── Unaudited 전용 (Lodger) ──
    lodger_name: lodgerName,
    lodger_nric: lodgerNric,
    lodger_address: lodgerAddress,
    lodger_phone: lodgerPhone,
    lodger_email: lodgerEmail,

    // ── Dividend ──
    dividend_type: dividendType,
    dividend_per_share: dividendPerShare,
    dividend_total_amount: dividendTotalAmount,
    dividend_record_date: dividendRecordDate,
    dividend_payment_date: dividendPaymentDate,

    // ── Remuneration ──
    directors_remuneration_total: directorsRemunerationTotal,

    // ── UPPER_CASE alias (레거시 템플릿 호환) ──
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: regNo,
  };
}

/**
 * docCode 또는 templateName 기반으로 AGM 문서 타입을 분류한다.
 * 하나의 PreSet에 포함된 문서 목록을 보고 어떤 입력 섹션을 활성화할지 결정.
 */
export interface AGMDocFlags {
  hasAdoption: boolean;       // DCR & MWR (Adoption of Audited/Unaudited Accounts)
  hasDividend: boolean;       // DCR & MWR (Declaration of Dividend)
  hasRemuneration: boolean;   // DCR & MWR (Directors' Remuneration)
  hasCoverLetter: boolean;    // Cover Letter
  hasFsReceipt: boolean;      // FS Receipt (Members / Auditors)
  hasAuditExemption: boolean; // Audit Exemption Certificate
  hasCertifiedExtract: boolean;
  isAudited: boolean;         // Audited 셋트인지
}

export function detectAGMDocFlags(documents: Array<{ templateName?: string; docCode?: string }>): AGMDocFlags {
  const names = (documents || []).map(d => (d.templateName || '').toLowerCase());
  const flags: AGMDocFlags = {
    hasAdoption: names.some(n => n.includes('adoption')),
    hasDividend: names.some(n => n.includes('dividend')),
    hasRemuneration: names.some(n => n.includes('remuneration')),
    hasCoverLetter: names.some(n => n.includes('cover letter')),
    hasFsReceipt: names.some(n => n.includes('fs receipt')),
    hasAuditExemption: names.some(n => n.includes('audit exemption')),
    hasCertifiedExtract: names.some(n => n.includes('certified extract')),
    isAudited: names.some(n => n.includes('audited')) && !names.some(n => n.includes('unaudited')),
  };
  return flags;
}
